import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { JsonLd, BreadcrumbJsonLd } from "../components/JsonLd";
import { SocialShare } from "../components/SocialShare";
import { formatReadingTime } from "../lib/readingTime";
import { safeHtml } from "../lib/sanitize";
import { Calendar, User, Eye, Clock } from "lucide-react";

const SITE = "https://fedevo20.fr";

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/articles/${slug}`).then(r => {
      setArticle(r.data);
      api.get("/articles").then(r2 => setRelated(r2.data.filter(a => a.slug !== slug).slice(0, 3)));
    });
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const prev = document.title;
    document.title = `${article.title} · Blog Fedevo 2.0`;
    return () => { document.title = prev; };
  }, [article]);

  if (!article) return <div className="p-10 text-center text-[color:var(--text-muted)]">Chargement...</div>;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.cover_image ? [article.cover_image] : undefined,
    "datePublished": article.published_at,
    "dateModified": article.published_at,
    "author": { "@type": "Organization", "name": article.author || "Fedevo 2.0" },
    "publisher": {
      "@type": "Organization",
      "name": "Fedevo 2.0 · Urban Media",
      "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_tv-broadcast-4/artifacts/p88vdzfy_fedevo%20logo%20tv%20jaune.png" },
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE}/blog/${article.slug}` },
    "keywords": (article.tags || []).join(", "),
    "inLanguage": "fr",
  };

  return (
    <article className="max-w-[800px] mx-auto px-6 py-12" data-testid="blog-article">
      <JsonLd data={articleLd} />
      <BreadcrumbJsonLd items={[
        { name: "Accueil", path: "/" },
        { name: "Blog", path: "/blog" },
        { name: article.title, path: `/blog/${article.slug}` },
      ]} />
      <Link to="/blog" className="inline-block mb-6 text-xs uppercase tracking-[0.3em] text-[color:var(--text-muted)] hover:text-[color:var(--accent)]">← Tous les articles</Link>
      <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-4">
        <Calendar className="w-3 h-3" />
        {new Date(article.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        <User className="w-3 h-3 ml-2" />
        {article.author}
        <span className="inline-flex items-center gap-1" data-testid="article-reading-time">
          <Clock className="w-3 h-3" /> {formatReadingTime(article.content)} de lecture
        </span>
        {typeof article.views === "number" && (
          <span className="inline-flex items-center gap-1" data-testid="article-views">
            <Eye className="w-3 h-3" /> {article.views} vue{article.views > 1 ? "s" : ""}
          </span>
        )}
        {article.tags?.map(t => (
          <span key={t} className="px-2 py-0.5 rounded-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] normal-case">{t}</span>
        ))}
      </div>
      <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] mb-6">{article.title}</h1>
      <p className="text-lg text-[color:var(--text-muted)] leading-relaxed mb-8">{article.excerpt}</p>
      {article.cover_image && (
        <img src={article.cover_image} alt={article.title} className="w-full rounded-2xl mb-8 aspect-[16/9] object-cover border border-[color:var(--border)]" />
      )}
      <div
        className="prose-article text-[16px] leading-relaxed space-y-5 text-[color:var(--text)]"
        dangerouslySetInnerHTML={safeHtml(article.content)}
      />
      <div className="mt-10 pt-6 border-t border-[color:var(--border)]">
        <SocialShare
          url={`${SITE}/blog/${article.slug}`}
          title={article.title}
          description={article.excerpt}
        />
      </div>
      {related.length > 0 && (
        <section className="mt-16 pt-10 border-t border-[color:var(--border)]">
          <h2 className="font-display text-3xl mb-6">Continuez la lecture</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {related.map(a => (
              <Link key={a.id} to={`/blog/${a.slug}`} className="card-hover rounded-xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--bg-elev)]">
                {a.cover_image && <img src={a.cover_image} alt="" className="aspect-[16/9] w-full object-cover" />}
                <div className="p-4">
                  <h3 className="font-display text-xl line-clamp-2">{a.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
